import React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { format } from "date-fns";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { Button, Dialog, Form, LongTextLabel, Toast } from "@sscs/ui";
import { DATE_FIELD_FORMAT } from "Constants";
import { Version } from "Types";
import { apiErrorToast } from "Utils";
import * as yup from "Utils/yup";
import styles from "./releaseDialog.module.scss";

const FormSchema = yup.object({
  release_date: yup
    .string()
    .required('"Release date" is required!')
    .typeError('"Release date" is required!'),
});

type EditVersionRequest = {
  versionId: string;
  body: {
    is_released: boolean;
    software_info: {
      release_date: string;
    };
  };
};

type DialogProps = {
  onClose: () => void;
  isOpen: boolean;
  version: Version;
  editVersion: (arg: EditVersionRequest) => {
    unwrap: () => Promise<void>;
  };
};

type ReleaseDialogFormData = {
  release_date: string;
};

const ReleaseDialogContent = ({
  onClose,
  version,
  editVersion,
}: Omit<DialogProps, "isOpen">): React.JSX.Element => {
  const formMethods = useForm<ReleaseDialogFormData>({
    resolver: yupResolver(FormSchema),
    defaultValues: { release_date: format(new Date(), DATE_FIELD_FORMAT) },
  });

  const onSubmit: SubmitHandler<ReleaseDialogFormData> = async (data) => {
    try {
      await editVersion({
        versionId: version.version_id,
        body: {
          is_released: true,
          software_info: {
            release_date: data.release_date,
          },
        },
      }).unwrap();
    } catch (error) {
      apiErrorToast(error, "Could not save approval changes");
      return;
    }

    Toast.show({
      title: "Release date updated",
      type: "success",
    });

    onClose();
  };

  return (
    <FormProvider {...formMethods}>
      <Form.Form onSubmit={formMethods.handleSubmit(onSubmit)}>
        <Dialog.Header>
          <Dialog.Header.Title>Mark as released</Dialog.Header.Title>
        </Dialog.Header>
        <Dialog.Body>
          <Dialog.Section>
            <Dialog.Section.Content>
              <div className={styles.content}>
                Do you want to mark version
                <LongTextLabel
                  label={version.software_info.version}
                  className={styles["release-version"]}
                />
                as released?
              </div>
              <Form.DatePicker
                name="release_date"
                label="Release date"
                errors={formMethods.formState.errors}
                isRequired
              />
            </Dialog.Section.Content>
          </Dialog.Section>
        </Dialog.Body>
        <Dialog.Footer>
          <Dialog.Footer.Actions>
            <Button color="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={formMethods.formState.isSubmitting}
            >
              Confirm
            </Button>
          </Dialog.Footer.Actions>
        </Dialog.Footer>
      </Form.Form>
    </FormProvider>
  );
};

const ReleaseDialog = ({
  isOpen,
  ...props
}: DialogProps): React.JSX.Element => (
  <Dialog.Frame isOpen={isOpen} onClose={props.onClose}>
    <ReleaseDialogContent {...props} />
  </Dialog.Frame>
);

export default ReleaseDialog;
